import { Dictionary, groupBy } from 'lodash'
import { InternationalizedObject } from 'src/@types'
import { Criteria_Config, Query_Root } from 'src/@types/graphql'

export const EXCLUSION_PREFIX = 'EXCLUSION_'
export const QUALITY_PREFIX = 'QUALITY_'

export const CRITERIA_RATINGS_VALUES = new Map([
  ['++', 4],
  ['+', 3],
  ['-', 2],
  ['--', 1],
])

export interface CriteriaFormValues {
  [key: string]: boolean | number | string | undefined

  version?: number
}

export type CriteriaConfigs = Array<
  Criteria_Config & {
    cgCode: string
    cgNames: InternationalizedObject
    cgActive: boolean
  }
>
export type CriteriaGroups = Dictionary<CriteriaConfigs>

interface CriteriaGroupsAndData {
  criteriaGroups: CriteriaGroups
  initialValues: CriteriaFormValues
}

export const createGroupsAndInitialData = (
  criteriaSelections:
    | Query_Root['project_assessment_criteria_selection']
    | Query_Root['milestone_assessment_criteria_selection']
    | undefined,
  criteriaGroupSortings: Query_Root['criteria_group_sorting_config'] | undefined,
  prefix: string,
  applicationCodeId: number | undefined,
  version?: number | undefined,
): CriteriaGroupsAndData => {
  const criteriaGroups = criteriaGroupSortings?.map((item) => item.criteria_group_config) ?? []

  criteriaGroups.forEach((item) => {
    item.criteria_configs = item.criteria_sorting_configs.map((item) => item.criteria_config)
  })

  /**
   * The structure we have *array of criteria groups, that has nested **array as
   * elements that are criteria configs and we need to filter the nested **array
   * to get the desired filter/merge of:
   *
   * 1. All active criteria configs
   * 2. Selected configs that are not active
   * 3. Filtered by application type id of the criteria config and the application type id of the project
   *
   * Example:
   * [ { id: 1, name: ..., criteria_configs:[ { id: 1, active: true }, { id: 2, active: false } ]} ]
   *
   */
  const criteriaConfig: CriteriaConfigs | undefined = criteriaGroups.flatMap((cg) =>
    cg.criteria_configs.map((cc) => ({
      ...cc,
      cgCode: cg.code,
      cgNames: cg.names,
      cgActive: cg.group_active,
    })),
  )

  const filterInactiveCC = criteriaConfig?.filter((cc) => {
    const hasValue = criteriaSelections?.some((s) => s.criteria_config_id === cc.id)
    if (hasValue) {
      return true
    }

    if (cc.cgActive) {
      if (cc.active) {
        return true
      }
    }

    return false
  })

  const filterApplicationType = filterInactiveCC?.filter((cc) => cc.application_type_ids?.includes(applicationCodeId))

  const groupConfigs: CriteriaGroups = groupBy(filterApplicationType, (cc) => cc.cgCode)

  const initialValues: CriteriaFormValues = criteriaSelections?.reduce(
    (aggregate: CriteriaFormValues, current: any) => {
      aggregate[`${prefix}${current.criteria_config_id}`] = current.rating ?? true
      return aggregate
    },
    { version: version || 0 },
  ) as CriteriaFormValues

  return {
    criteriaGroups: groupConfigs,
    initialValues,
  }
}

/**
 * Calculates the average rating from the provided criteria values.
 *
 * @param {CriteriaConfigs} criteriaConfigs - Array of criteria configuration objects.
 * @param {CriteriaFormValues | undefined} values - Form values containing ratings for each criterion.
 * @returns {number} The average rating rounded to two decimal places, or 0 if no criteria are provided or values are undefined.
 */
export const getAvgRatingFromCriteriaValues = (
  criteriaConfigs: CriteriaConfigs,
  values: CriteriaFormValues | undefined,
): number => {
  if (criteriaConfigs.length === 0 || values === undefined) {
    return 0
  }

  // add ratings for each criteria in the group to get the average of that group
  const total = criteriaConfigs.reduce((sum, i) => {
    const criteriaRating = values?.[`${QUALITY_PREFIX}${i.id}`]?.toString() ?? ''

    return sum + (CRITERIA_RATINGS_VALUES.get(criteriaRating) || 0)
  }, 0)

  const average = total / criteriaConfigs.length

  return parseFloat(average.toFixed(2))
}

/**
 * Filters the criteria configurations to include only those related to quality (prefixed with '(Q)').
 *
 * @param {CriteriaConfigs} criteriaConfigs - Array of criteria configuration objects.
 * @returns {CriteriaConfigs} Filtered array of criteria configurations with IDs starting with '(Q)'.
 */
export const getQCriteria = (criteriaConfigs: CriteriaConfigs): CriteriaConfigs => {
  return criteriaConfigs.filter((j) => j.criteria_id.startsWith('(Q)'))
}
